/**
 * The plumbing every api/ module stands on: the three HTTP verbs (always with
 * `credentials: "include"`), `call()`'s one recovery for offline/CORS/down,
 * and the tolerant-parse helpers.
 *
 * Two rules about those helpers, learned the hard way:
 *
 *   1. They are TOLERANT OF SHAPE, not of naming. `num()` absorbing a missing
 *      field is how six renamed fields rendered as plausible zeros for a whole
 *      milestone ("spent 0 cr", on every inbox card). A parser reads exactly
 *      the server's spelling — never a second accepted name — and the seam
 *      tests (`inbox-contract.test.ts`) round-trip the server's real
 *      serializers through the real parsers so a rename fails a test, not a
 *      demo.
 *   2. `num` vs `nullableNum` is a statement about meaning, not style: 0 is
 *      right for money and wrong for a rank — a null percentile floored to 0
 *      reads as "Bottom 1%", a confident position for a knock the door just
 *      said it cannot place (E-63).
 */
import { API_URL as CONFIGURED_API_URL, WEB_ORIGIN as CONFIGURED_WEB_ORIGIN } from "../config";
import type { CheckoutOpening } from "../checkout";

/**
 * Re-exported rather than defined here: `lib/config` is the single place a
 * build's origins are decided, and every existing importer of `API_URL` — this
 * file, `lib/sse`, the share helpers — keeps working unchanged.
 */
export const API_URL = CONFIGURED_API_URL;
export const WEB_ORIGIN =
  typeof window === "undefined" ? CONFIGURED_WEB_ORIGIN : window.location.origin;

/** Where auth providers send the browser back to. Same SPA, no reload cost. */
export const CALLBACK_URL = `${WEB_ORIGIN}/`;

export async function post(path: string, body: unknown): Promise<Response> {
  return fetch(`${API_URL}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function patch(path: string, body: unknown): Promise<Response> {
  return fetch(`${API_URL}${path}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function get(path: string): Promise<Response> {
  return fetch(`${API_URL}${path}`, { credentials: "include" });
}

export type RawError = { error?: { code?: string; details?: unknown } };

/**
 * THE SERVER SAYS A LEGAL VERSION HAS TO BE ACCEPTED (ruling R-0919-2).
 *
 * Handled HERE, in the one function every write goes through, and nowhere
 * else — the same reasoning that put the lost session in `query-client.ts`.
 * The blocking screen is drawn off `/me`, and a tab that was open before the
 * rollout was published holds a `/me` that says nothing is pending. Its next
 * write meets a 403, and this is what turns that refusal into a re-read, so
 * the poster appears on the tab that tried rather than on the next reload.
 *
 * The client still decides nothing: it re-reads, and the server says what is
 * pending. `main.tsx` registers the one handler.
 */
export const LEGAL_ACCEPTANCE_REQUIRED = "LEGAL_ACCEPTANCE_REQUIRED";

let onLegalRequired: (() => void) | null = null;

export function setLegalRequiredHandler(handler: (() => void) | null): void {
  onLegalRequired = handler;
}

export async function call(
  run: () => Promise<Response>,
): Promise<{ res: Response; body: unknown } | null> {
  try {
    const res = await run();
    const body = await res.json().catch(() => ({}));
    if (res.status === 403 && (body as RawError).error?.code === LEGAL_ACCEPTANCE_REQUIRED) {
      onLegalRequired?.();
    }
    return { res, body };
  } catch {
    return null; // offline, CORS, API down — one recovery for all of them
  }
}

export function num(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/**
 * A number, or null when the server means "there is no value here". `num()`
 * floors a missing value to 0, which is right for a count and wrong for a rank:
 * a null percentile rendered as 0 reads as "Bottom 1%" — a confident position
 * for a knock the door has just said it cannot place (E-63).
 */
export function nullableNum(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function strings(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter((s): s is string => typeof s === "string" && s.trim().length > 0)
    : [];
}

/** A handle is written both ways; the wire wants it one way. */
export function bareHandle(handle: string): string {
  return handle.trim().replace(/^@/, "");
}

// `detailNum` stood here — the shortfall off a 402, read under whichever of two
// names the server used for it. Ruling R-0916-1 deleted the shortfall with the
// credit product: a refusal now carries the three roads out of it and not one
// number, so there is no figure left in an error's details to dig for.

export function detailsOf(body: RawError): Record<string, unknown> {
  const details = body.error?.details;
  return details && typeof details === "object" ? (details as Record<string, unknown>) : {};
}

/** A string, or the empty string — the caller decides whether that is renderable. */
export function textOf(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** A string with something in it, or `null` — for the optional, hide-if-absent fields. */
export function optTextOf(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/**
 * The two shapes a started checkout comes back in, read into the one the modal
 * renders from (ruling R-0825-2, `lib/checkout.ts`).
 *
 * ONE ENDPOINT ANSWERS IN THEM NOW — `/plan/subscribe`, for a rung. It had a
 * sibling, `/billing/checkout` for a pack, and ruling R-0916-1 deleted it along
 * with the thing a pack was; the reading stays here rather than moving into the
 * plan module, because the two SHAPES are Stripe's and not the plan lane's.
 *
 * `checkout_url` is FakeStripe's, and it arrives as a PATH: it is hosted by the
 * api itself. Resolving it against the api base is right for that — and
 * resolving against the SPA instead lands on the SPA's catch-all, which renders
 * Home and looks, for one confusing moment, like a checkout that silently did
 * nothing. Under the DEV PROXY the api base is the same-origin empty string and
 * `new URL(path, "")` THROWS, which once dressed a working checkout as "Nob is
 * not answering"; the origin fallback is that case.
 *
 * `client_secret` is Stripe's own, and it needs the publishable key beside it
 * or nothing can be mounted — so a payload carrying one without the other is
 * read as no opening at all rather than as a modal that would open empty.
 */
export function readOpening(body: unknown): CheckoutOpening | null {
  const b = (body ?? {}) as Record<string, unknown>;
  const secret = b["client_secret"];
  const key = b["publishable_key"];
  if (typeof secret === "string" && secret && typeof key === "string" && key) {
    return { kind: "embedded", clientSecret: secret, publishableKey: key };
  }
  const url = b["checkout_url"];
  if (typeof url !== "string" || !url) return null;
  try {
    return { kind: "page", url: new URL(url, API_URL || WEB_ORIGIN).toString() };
  } catch {
    return null;
  }
}
